import { PayloadAction, createReducer } from "@reduxjs/toolkit";
import { AnyAction } from "redux";

import ViridisUtils from "../../components/utilities/ViridisUtils";
import type { IChemicalSearchModelResult } from "../../data/ChemicalSearchModelResult";
import type {
    AnalyzeReactionRequest,
    AnalyzeReactionResponse,
    ReactionAnalysisProgressUpdate,
    ReactionPropertyValue,
} from "../../data/models/schemas";
import {
    IReactionChemical,
    ILibraryReaction,
    ILibraryMultiStepReaction,
    IReactionChemicalType,
} from "../actions/library";
import {
    analyzeMultiStepReaction,
    analyzeMultiStepReactionRealtime,
    analyzeReaction,
    analyzeReactionRealtime,
    convertMultiStepReaction,
    convertReaction,
    getReactionChemicalTypes,
    hideImportModal,
    hideMultiStepStepModal,
    hideReactionChemicalEditModal,
    hideReactionChemicalReactionLinkModal,
    hideReactionsEditModal,
    hideReactionsLoadModal,
    hideUploadModal,
    IConvertMultiStepReactionResponse,
    IConvertReactionResponse,
    IReactionAnalysisRealtimeSocketError,
    IMultiStepAnalysisResponse,
    IRouteAnalysisRealtimeSocketError,
    IPredictionCandidate,
    IPredictionModalState,
    ISimplePredictionResponse,
    addPredictionCandidates,
    hidePredictionModal,
    predictComponents,
    predictProducts,
    predictReactants,
    predictReagents,
    predictCatalyst,
    predictSolvent,
    selectPredictionCandidate,
    setPredictionCandidates,
    showPredictionModal,
    unselectPredictionCandidate,
    removeReactionChemical,
    removeReactionChemicalLinkedChemical,
    removeReactionChemicalLinkedReaction,
    searchReactionChemical,
    setAnalyzerMode,
    setCurrentMultiStepReaction,
    setMultiStepActiveStep,
    setReactionChemicalDescription,
    setReactionChemicalAmountUnit,
    setReactionChemicalAmountValue,
    setReactionChemicalMassUnit,
    setReactionChemicalMassValue,
    setReactionChemicalName,
    setReactionChemicalType,
    setReactionDescription,
    setReactionName,
    setShownMultiStepReaction,
    setShownReaction,
    setShownReactionName,
    showImportModal,
    showMultiStepStepModal,
    showReactionChemicalEditModal,
    showReactionChemicalReactionLinkModal,
    showReactionsEditModal,
    showReactionsLoadModal,
    showUploadModal,
    submitReactionChemicalEditModal,
    submitReactionChemicalLinkedReaction,
    submitReactionsEditModal,
    submitReactionsLoadModal,
} from "../actions/reactions";
import { resolveRsaaErrorMessage } from "../utils/rsaaErrorMessage";
import {
    buildReactionAmountFromUnit,
    getReactionAmountEntry,
    sanitizeReactionAmount,
} from "../../utils/reactionAmount";

export type IMultiStepReaction = ILibraryMultiStepReaction & {
    reactions?: IReactionMap;
};

export type IReaction = ILibraryReaction;

export type IShownReaction = {
    reaction: IReaction;
    properties: IReactionPropertyMap;
    analysisResults?: IReactionPropertyResponse;
};

export type IReactionPropertyRequest = AnalyzeReactionRequest;
export type IReactionPropertyResponse = AnalyzeReactionResponse;

export type IReactionMap = {
    [key: string]: IReaction;
};

export type IReactionPropertyMap = {
    [key: string]: IReactionProperty;
};

export type IReactionProperty = ReactionPropertyValue;
export type IReactionPropertyValue = ReactionPropertyValue;

export type IReactionAnalysisProgressState = {
    percentComplete: number;
    message?: string;
    phase?: string;
    currentStep?: number;
    totalSteps?: number;
    chemicalsAnalyzed?: number;
    totalChemicals?: number;
    reactionsAnalyzed?: number;
    totalReactions?: number;
    elapsedMillis?: number;
    cacheHit?: boolean;
};

// IReactionChemical is now imported from library actions

const normalizePredictionRole = (
    role: string | undefined,
    fallback: string
): string => {
    if (!role) {
        return fallback;
    }
    return role.toLowerCase();
};

const buildPredictionCandidates = (
    response: ISimplePredictionResponse | undefined,
    fallbackRole: string,
    idPrefix: string
): IPredictionCandidate[] => {
    if (!response?.predictions) {
        return [];
    }

    return response.predictions.flatMap((group, groupIndex) =>
        (group.components || []).map((component, componentIndex) => {
            const confidence =
                component.confidence ?? group.groupConfidence ?? 0;
            return {
                id: `${idPrefix}-${groupIndex}-${componentIndex}-${ViridisUtils.getUuid()}`,
                name: component.name || "Unknown",
                smiles: component.smiles || "",
                confidence,
                role: normalizePredictionRole(component.role, fallbackRole),
                rationale: component.rationale || "",
            };
        })
    );
};

const buildInitialAnalysisProgress = (): IReactionAnalysisProgressState => ({
    percentComplete: 0,
    phase: "VALIDATING",
});

const buildCompleteAnalysisProgress = (): IReactionAnalysisProgressState => ({
    percentComplete: 100,
    phase: "COMPLETE",
});

const normalizeAnalysisProgress = (
    progress: ReactionAnalysisProgressUpdate | undefined
): IReactionAnalysisProgressState => ({
    percentComplete: Math.max(0, Math.min(100, progress?.percentComplete ?? 0)),
    message: progress?.message,
    phase: progress?.phase,
    currentStep: progress?.currentStep,
    totalSteps: progress?.totalSteps,
    chemicalsAnalyzed: progress?.chemicalsAnalyzed,
    totalChemicals: progress?.totalChemicals,
    reactionsAnalyzed: progress?.reactionsAnalyzed,
    totalReactions: progress?.totalReactions,
    elapsedMillis: progress?.elapsedMillis,
    cacheHit: progress?.cacheHit,
});

const resolveRealtimeAnalysisErrorMessage = (
    payload:
        | IReactionAnalysisRealtimeSocketError
        | IRouteAnalysisRealtimeSocketError
        | undefined,
    fallback: string
): string =>
    payload?.message ||
    (payload?.code ? `analysis.error.${payload.code}` : undefined) ||
    fallback;

export type IReactionsState = {
    showEditModal: boolean;
    showLoadModal: boolean;
    showChemicalEditModal: boolean;
    showReactionChemicalReactionLinkModal: boolean;
    showUploadModal: boolean;
    showImportModal: boolean;
    shownReaction: IShownReaction;
    currentReaction: IReaction;
    currentReactionChemical: IReactionChemical;
    chemical_types: IReactionChemicalType[];
    analyzerMode: "single" | "multi-step";
    shownMultiStepReaction: IMultiStepReaction | null;
    currentMultiStepReaction: IMultiStepReaction | null;
    multiStepAnalysisResults?: IMultiStepAnalysisResponse;
    isAnalyzingReaction: boolean;
    reactionAnalysisProgress?: IReactionAnalysisProgressState;
    reactionAnalysisError?: string;
    isAnalyzingMultiStep: boolean;
    multiStepAnalysisProgress?: IReactionAnalysisProgressState;
    multiStepAnalysisError?: string;
    showMultiStepStepModal: boolean;
    multiStepActiveStepId?: string;
    multiStepActiveStepIndex: number;
    prediction: IPredictionModalState;
};

const getDefaultState = (): IReactionsState => {
    return {
        showEditModal: false,
        showLoadModal: false,
        showChemicalEditModal: false,
        showReactionChemicalReactionLinkModal: false,
        showUploadModal: false,
        showImportModal: false,
        shownReaction: null,
        currentReaction: getDefaultReaction(),
        currentReactionChemical: getDefaultReactionChemical(""),
        chemical_types: [],
        analyzerMode: "single",
        shownMultiStepReaction: null,
        currentMultiStepReaction: null,
        multiStepAnalysisResults: undefined,
        isAnalyzingReaction: false,
        reactionAnalysisProgress: undefined,
        reactionAnalysisError: undefined,
        isAnalyzingMultiStep: false,
        multiStepAnalysisProgress: undefined,
        multiStepAnalysisError: undefined,
        showMultiStepStepModal: false,
        multiStepActiveStepId: undefined,
        multiStepActiveStepIndex: 0,
        prediction: {
            show: false,
            type: null,
            loading: false,
            error: null,
            candidates: [],
            selectedCandidates: [],
        },
    };
};

const getDefaultReaction = (): IReaction => ({
    ui_id: ViridisUtils.getUuid(),
    name: "",
    chemicals: [],
});

const getShownReaction = (reaction?: IReaction): IShownReaction => ({
    reaction,
    properties: {},
});

const getDefaultReactionChemical = (
    reaction_ui_id?: string
): IReactionChemical => ({
    ui_id: ViridisUtils.getUuid(),
    name: "",
    reaction_ui_id: reaction_ui_id || ViridisUtils.getUuid(),
    chemical_type: "Reactant",
});

// Known reagent and solvent SMILES patterns
const KNOWN_REAGENTS = new Set([
    "S(=O)(Cl)Cl", // SOCl2 - thionyl chloride
    "C(=O)(C(=O)Cl)Cl", // COCl2 - phosgene
    "ClC(=O)C(=O)Cl", // oxalyl chloride
    "O=S(=O)(O)O", // H2SO4 - sulfuric acid
    "ClCCl", // DCM - dichloromethane (reagent)
    "P(Cl)(Cl)(Cl)", // PCl3 - phosphorus trichloride
    "P(Cl)(Cl)(Cl)(Cl)Cl", // PCl5 - phosphorus pentachloride
]);

const KNOWN_SOLVENTS = new Set([
    "C(Cl)(Cl)(Cl)Cl", // CCl4 - carbon tetrachloride
    "ClCCl", // DCM - dichloromethane (can be solvent)
    "CCO", // ethanol
    "CO", // methanol
    "COCCOC", // DME - dimethoxyethane
    "c1ccccc1", // benzene
    "Cc1ccccc1", // toluene
    "CCCCCCCC", // octane
    "O", // water
]);

const KNOWN_CATALYSTS = new Set([
    "[Pd]", // palladium
    "[Pt]", // platinum
    "[Ni]", // nickel
    "[Cu]", // copper
    "[Rh]", // rhodium
    "[Ru]", // ruthenium
]);

// Fix chemical types based on SMILES reaction and chemical knowledge
const fixChemicalTypes = (reaction: IReaction): IReaction => {
    if (!reaction.chemicals || !reaction.reaction_smiles) {
        return reaction;
    }

    // Parse reaction SMILES: reactants>reagents>products
    const smilesParts = reaction.reaction_smiles.split(">");
    if (smilesParts.length !== 3) {
        return reaction; // Invalid format, don't modify
    }

    const [reactantsPart, reagentsPart, productsPart] = smilesParts;
    const reactantSmiles = reactantsPart.split(".").filter((s) => s.length > 0);
    const reagentSmiles = reagentsPart.split(".").filter((s) => s.length > 0);
    const productSmiles = productsPart.split(".").filter((s) => s.length > 0);

    // Create corrected chemicals array
    const correctedChemicals: IReactionChemical[] = reaction.chemicals.map(
        (chemical): IReactionChemical => {
            // If already correctly assigned as Product, keep it
            if (chemical.chemical_type === "Product") {
                return chemical;
            }

            // Get the chemical's SMILES for matching
            const chemicalSmiles = chemical.smiles;
            if (!chemicalSmiles) {
                return chemical; // No SMILES to analyze
            }

            // Check if this chemical is in the reagents part of the reaction SMILES
            if (reagentSmiles.includes(chemicalSmiles)) {
                // Determine if it's a reagent, solvent, or catalyst
                let newType: IReactionChemicalType = "Reagent"; // default for reagents section

                if (KNOWN_CATALYSTS.has(chemicalSmiles)) {
                    newType = "Catalyst";
                } else if (KNOWN_SOLVENTS.has(chemicalSmiles)) {
                    newType = "Solvent";
                } else if (KNOWN_REAGENTS.has(chemicalSmiles)) {
                    newType = "Reagent";
                }

                return {
                    ...chemical,
                    chemical_type: newType,
                };
            }

            // Check if this chemical is in the reactants part
            if (reactantSmiles.includes(chemicalSmiles)) {
                return {
                    ...chemical,
                    chemical_type: "Reactant",
                };
            }

            // Check if this chemical is in the products part
            if (productSmiles.includes(chemicalSmiles)) {
                return {
                    ...chemical,
                    chemical_type: "Product",
                };
            }

            // Fallback: use chemical knowledge based on SMILES patterns
            if (KNOWN_CATALYSTS.has(chemicalSmiles)) {
                return {
                    ...chemical,
                    chemical_type: "Catalyst",
                };
            } else if (KNOWN_SOLVENTS.has(chemicalSmiles)) {
                return {
                    ...chemical,
                    chemical_type: "Solvent",
                };
            } else if (KNOWN_REAGENTS.has(chemicalSmiles)) {
                return {
                    ...chemical,
                    chemical_type: "Reagent",
                };
            }

            // Keep original assignment if no better match found
            return chemical;
        }
    );

    console.log(
        "fixChemicalTypes - Corrected chemicals:",
        correctedChemicals.map((c) => ({
            name: c.name,
            type: c.chemical_type,
            smiles: c.smiles,
        }))
    );

    return {
        ...reaction,
        chemicals: correctedChemicals,
    };
};

export const reactions = createReducer(getDefaultState(), (builder) => {
    builder.addCase(showReactionsEditModal, (state, action) => {
        state.showEditModal = true;
        if (action.payload) {
            state.currentReaction = action.payload;
        } else {
            state.currentReaction = getDefaultReaction();
        }
    });
    builder.addCase(submitReactionsEditModal, (state, action) => {
        state.shownReaction = getShownReaction(action.payload);
        state.showEditModal = false;
    });
    builder.addCase(hideReactionsEditModal, (state) => {
        state.showEditModal = false;
    });
    builder.addCase(showReactionsLoadModal, (state) => {
        state.showLoadModal = true;
    });
    builder.addCase(submitReactionsLoadModal, (state, action) => {
        state.shownReaction = getShownReaction(action.payload);
        state.showLoadModal = false;
    });
    builder.addCase(setShownReaction, (state, action) => {
        state.shownReaction = getShownReaction(action.payload);
    });
    builder.addCase(hideReactionsLoadModal, (state) => {
        state.showLoadModal = false;
    });
    builder.addCase(showReactionChemicalEditModal, (state, action) => {
        state.showChemicalEditModal = true;
        if (action.payload) {
            state.currentReactionChemical = action.payload;
        } else {
            state.currentReactionChemical = getDefaultReactionChemical(
                state.currentReaction?.ui_id
            );
        }
    });
    builder.addCase(
        submitReactionChemicalEditModal,
        (state, action: { payload: IReactionChemical }) => {
            const nextChemical: IReactionChemical = {
                ...action.payload,
                amount: sanitizeReactionAmount(action.payload.amount),
            };
            const existingIndex = state.currentReaction.chemicals.findIndex(
                (c) => c.ui_id === nextChemical.ui_id
            );
            if (existingIndex >= 0) {
                state.currentReaction.chemicals[existingIndex] = nextChemical;
            } else {
                state.currentReaction.chemicals.push(nextChemical);
            }
            state.showChemicalEditModal = false;
            state.shownReaction = getShownReaction(state.currentReaction);
            state.currentReactionChemical = getDefaultReactionChemical(
                state.currentReaction?.ui_id
            );
        }
    );
    builder.addCase(hideReactionChemicalEditModal, (state) => {
        state.showChemicalEditModal = false;
        state.currentReactionChemical = getDefaultReactionChemical(
            state.currentReaction?.ui_id
        );
    });
    builder.addCase(showReactionChemicalReactionLinkModal, (state) => {
        state.showReactionChemicalReactionLinkModal = true;
    });
    builder.addCase(hideReactionChemicalReactionLinkModal, (state) => {
        state.showReactionChemicalReactionLinkModal = false;
    });
    builder.addCase(showUploadModal, (state) => {
        state.showUploadModal = true;
    });
    builder.addCase(hideUploadModal, (state) => {
        state.showUploadModal = false;
    });
    builder.addCase(showImportModal, (state) => {
        state.showImportModal = true;
    });
    builder.addCase(hideImportModal, (state) => {
        state.showImportModal = false;
    });
    builder.addCase(setReactionName, (state, action) => {
        state.currentReaction.name = action.payload;
    });
    builder.addCase(setReactionDescription, (state, action) => {
        state.currentReaction.description = action.payload;
    });
    builder.addCase(setShownReactionName, (state, action) => {
        if (state.shownReaction?.reaction) {
            state.shownReaction.reaction.name = action.payload;
        }
    });
    builder.addCase(
        removeReactionChemical,
        (state, action: { payload: IReactionChemical }) => {
            const existingIndex = state.currentReaction.chemicals.findIndex(
                (c) => c.ui_id === action.payload.ui_id
            );
            if (existingIndex >= 0) {
                state.currentReaction.chemicals.splice(existingIndex, 1);
            }
        }
    );
    builder.addCase(setReactionChemicalName, (state, action) => {
        state.currentReactionChemical.name = action.payload;
    });
    builder.addCase(setReactionChemicalDescription, (state, action) => {
        state.currentReactionChemical.description = action.payload;
    });
    builder.addCase(setReactionChemicalType, (state, action) => {
        state.currentReactionChemical.chemical_type = action.payload;
    });
    builder.addCase(setReactionChemicalMassValue, (state, action) => {
        state.currentReactionChemical.amount = {
            ...(state.currentReactionChemical.amount || {}),
            massValue: action.payload,
        };
    });
    builder.addCase(setReactionChemicalMassUnit, (state, action) => {
        state.currentReactionChemical.amount = {
            ...(state.currentReactionChemical.amount || {}),
            massUnit: action.payload,
        };
    });
    builder.addCase(setReactionChemicalAmountValue, (state, action) => {
        state.currentReactionChemical.amount = buildReactionAmountFromUnit(
            action.payload.unit,
            action.payload.value
        );
    });
    builder.addCase(setReactionChemicalAmountUnit, (state, action) => {
        const nextValue = getReactionAmountEntry(
            state.currentReactionChemical.amount
        )?.value;

        state.currentReactionChemical.amount = buildReactionAmountFromUnit(
            action.payload,
            nextValue
        );
    });
    builder.addCase(
        searchReactionChemical.success,
        (state, action: PayloadAction<IChemicalSearchModelResult>) => {
            const models = action.payload?.models || [];
            if (models.length === 1) {
                state.currentReactionChemical.chemical = models[0];
            }
        }
    );
    builder.addCase(removeReactionChemicalLinkedChemical, (state) => {
        state.currentReactionChemical.chemical = undefined;
    });
    builder.addCase(submitReactionChemicalLinkedReaction, (state, action) => {
        state.currentReactionChemical.reaction_ui_id = action.payload;
        state.showReactionChemicalReactionLinkModal = false;
    });
    builder.addCase(removeReactionChemicalLinkedReaction, (state) => {
        state.currentReactionChemical.reaction_ui_id = undefined;
    });
    builder.addCase(getReactionChemicalTypes.success, (state, action) => {
        state.chemical_types = action.payload;
    });
    builder.addCase(analyzeReaction.request, (state) => {
        state.isAnalyzingReaction = true;
        state.reactionAnalysisError = undefined;
        state.reactionAnalysisProgress = undefined;
        if (state.shownReaction) {
            state.shownReaction.analysisResults = undefined;
        }
    });
    builder.addCase(analyzeReaction.success, (state, action) => {
        const response = action.payload as AnalyzeReactionResponse;
        // The new API returns the full analysis data directly, not wrapped in a properties object
        state.isAnalyzingReaction = false;
        state.reactionAnalysisError = undefined;
        state.reactionAnalysisProgress = undefined;
        if (!state.shownReaction) {
            state.shownReaction = getShownReaction(response.reaction);
        }
        state.shownReaction.analysisResults = response;
    });
    builder.addCase(analyzeReaction.error, (state, action) => {
        state.isAnalyzingReaction = false;
        state.reactionAnalysisError = resolveRsaaErrorMessage(
            action.payload,
            "Reaction analysis failed"
        );
        state.reactionAnalysisProgress = undefined;
    });
    builder.addCase(analyzeReactionRealtime.request, (state) => {
        state.isAnalyzingReaction = true;
        state.reactionAnalysisError = undefined;
        state.reactionAnalysisProgress = buildInitialAnalysisProgress();
        if (state.shownReaction) {
            state.shownReaction.analysisResults = undefined;
        }
    });
    builder.addCase(analyzeReactionRealtime.update, (state, action) => {
        state.isAnalyzingReaction = true;
        state.reactionAnalysisProgress = normalizeAnalysisProgress(
            action.payload as ReactionAnalysisProgressUpdate
        );
    });
    builder.addCase(analyzeReactionRealtime.success, (state, action) => {
        const response = action.payload as AnalyzeReactionResponse;
        state.isAnalyzingReaction = false;
        state.reactionAnalysisError = undefined;
        state.reactionAnalysisProgress = buildCompleteAnalysisProgress();
        if (!state.shownReaction) {
            state.shownReaction = getShownReaction(response.reaction);
        }
        state.shownReaction.analysisResults = response;
    });
    builder.addCase(analyzeReactionRealtime.error, (state, action) => {
        state.isAnalyzingReaction = false;
        state.reactionAnalysisError = resolveRealtimeAnalysisErrorMessage(
            action.payload,
            "Reaction analysis failed"
        );
        state.reactionAnalysisProgress = undefined;
    });
    builder.addCase(convertReaction.success, (state, action: AnyAction) => {
        const name = action.meta?.reactionName as string;
        const response = action.payload as IConvertReactionResponse;

        // According to OpenAPI spec, response should have reaction property
        if (!response.reaction || !response.reaction.ui_id) {
            // For save operations (outputFormat specified), payload might not have reaction
            // Don't update shown reaction for save operations
            console.log(
                "convertReaction.success - No reaction in response, likely save operation"
            );
            return;
        }

        let reaction = response.reaction;
        console.log(
            "convertReaction.success - Imported reaction chemicals:",
            reaction.chemicals?.map((c) => ({
                name: c.name,
                type: c.chemical_type,
            }))
        );

        // Fix chemical type assignments based on SMILES analysis
        reaction = fixChemicalTypes(reaction);

        // Apply the reaction name from meta if provided
        if (name) {
            reaction = {
                ...reaction,
                name,
            };
        }
        state.shownReaction = getShownReaction(reaction);
        // Close import modal on successful conversion
        state.showImportModal = false;
    });
    builder.addCase(
        convertMultiStepReaction.success,
        (state, action: AnyAction) => {
            const name = action.meta?.reactionName as string;
            const description = action.meta?.reactionDescription as string;
            const response =
                action.payload as IConvertMultiStepReactionResponse;

            console.log(
                "Multi-step conversion response in main reducer:",
                response
            );

            // According to OpenAPI spec, response should have reactionRoute property
            if (!response.reactionRoute || !response.reactionRoute.ui_id) {
                console.log(
                    "convertMultiStepReaction.success - No reactionRoute in response, likely save operation"
                );
                return;
            }

            let multiStepReaction = response.reactionRoute;

            // Apply the name and description from meta if provided
            if (name || description) {
                multiStepReaction = {
                    ...multiStepReaction,
                    name:
                        name ||
                        multiStepReaction.name ||
                        "Imported Multi-Step Reaction",
                    description: description || multiStepReaction.description,
                };
            }

            // Set the converted multi-step reaction as shown
            state.shownMultiStepReaction = multiStepReaction;
            // Switch to multi-step mode when importing a multi-step reaction
            state.analyzerMode = "multi-step";

            // Close import modal on successful conversion
            state.showImportModal = false;
        }
    );

    // Multi-step reaction analyzer actions
    builder.addCase(setAnalyzerMode, (state, action) => {
        state.analyzerMode = action.payload;
    });
    builder.addCase(setShownMultiStepReaction, (state, action) => {
        state.shownMultiStepReaction = action.payload;
    });
    builder.addCase(setCurrentMultiStepReaction, (state, action) => {
        state.currentMultiStepReaction = action.payload;
    });
    builder.addCase(showMultiStepStepModal, (state, action) => {
        state.showMultiStepStepModal = true;
        state.multiStepActiveStepId = action.payload.stepId;
        state.multiStepActiveStepIndex = action.payload.stepIndex;
    });
    builder.addCase(hideMultiStepStepModal, (state) => {
        state.showMultiStepStepModal = false;
        state.multiStepActiveStepId = undefined;
    });
    builder.addCase(setMultiStepActiveStep, (state, action) => {
        state.multiStepActiveStepIndex = action.payload;
    });

    // Multi-step analysis actions
    builder.addCase(analyzeMultiStepReaction.request, (state) => {
        state.isAnalyzingMultiStep = true;
        state.multiStepAnalysisError = undefined;
        state.multiStepAnalysisProgress = undefined;
        state.multiStepAnalysisResults = undefined;
    });
    builder.addCase(analyzeMultiStepReaction.success, (state, action) => {
        state.isAnalyzingMultiStep = false;
        state.multiStepAnalysisResults = action.payload;
        state.multiStepAnalysisError = undefined;
        state.multiStepAnalysisProgress = undefined;
    });
    builder.addCase(analyzeMultiStepReaction.error, (state, action) => {
        state.isAnalyzingMultiStep = false;
        state.multiStepAnalysisError = resolveRsaaErrorMessage(
            action.payload,
            "Multi-step analysis failed"
        );
        state.multiStepAnalysisProgress = undefined;
        state.multiStepAnalysisResults = undefined;
    });
    builder.addCase(analyzeMultiStepReactionRealtime.request, (state) => {
        state.isAnalyzingMultiStep = true;
        state.multiStepAnalysisError = undefined;
        state.multiStepAnalysisProgress = buildInitialAnalysisProgress();
        state.multiStepAnalysisResults = undefined;
    });
    builder.addCase(
        analyzeMultiStepReactionRealtime.update,
        (state, action) => {
            state.isAnalyzingMultiStep = true;
            state.multiStepAnalysisProgress = normalizeAnalysisProgress(
                action.payload as ReactionAnalysisProgressUpdate
            );
        }
    );
    builder.addCase(
        analyzeMultiStepReactionRealtime.success,
        (state, action) => {
            state.isAnalyzingMultiStep = false;
            state.multiStepAnalysisResults =
                action.payload as IMultiStepAnalysisResponse;
            state.multiStepAnalysisError = undefined;
            state.multiStepAnalysisProgress = buildCompleteAnalysisProgress();
        }
    );
    builder.addCase(analyzeMultiStepReactionRealtime.error, (state, action) => {
        state.isAnalyzingMultiStep = false;
        state.multiStepAnalysisError = resolveRealtimeAnalysisErrorMessage(
            action.payload,
            "Multi-step analysis failed"
        );
        state.multiStepAnalysisProgress = undefined;
        state.multiStepAnalysisResults = undefined;
    });

    // Prediction modal actions
    builder.addCase(showPredictionModal, (state, action) => {
        state.prediction.show = true;
        state.prediction.type = action.payload;
        state.prediction.candidates = [];
        state.prediction.selectedCandidates = [];
        state.prediction.error = null;
    });
    builder.addCase(hidePredictionModal, (state) => {
        state.prediction.show = false;
        state.prediction.type = null;
        state.prediction.loading = false;
        state.prediction.error = null;
        state.prediction.candidates = [];
        state.prediction.selectedCandidates = [];
    });
    builder.addCase(setPredictionCandidates, (state, action) => {
        state.prediction.candidates = action.payload;
        state.prediction.selectedCandidates = [];
    });
    builder.addCase(selectPredictionCandidate, (state, action) => {
        if (!state.prediction.selectedCandidates.includes(action.payload)) {
            state.prediction.selectedCandidates.push(action.payload);
        }
    });
    builder.addCase(unselectPredictionCandidate, (state, action) => {
        state.prediction.selectedCandidates =
            state.prediction.selectedCandidates.filter(
                (id) => id !== action.payload
            );
    });
    builder.addCase(addPredictionCandidates, (state, action) => {
        if (!state.currentReaction) {
            return;
        }

        // Add selected candidates as chemicals to the current reaction
        const selectedCandidates = action.payload.filter((candidate) =>
            state.prediction.selectedCandidates.includes(candidate.id)
        );

        selectedCandidates.forEach((candidate) => {
            const newChemical: IReactionChemical = {
                ui_id: ViridisUtils.getUuid(),
                name: candidate.name,
                smiles: candidate.smiles,
                reaction_ui_id: state.currentReaction.ui_id,
                chemical_type:
                    candidate.role === "reactant"
                        ? "Reactant"
                        : candidate.role === "reagent"
                          ? "Reagent"
                          : candidate.role === "product"
                            ? "Product"
                            : candidate.role === "catalyst"
                              ? "Catalyst"
                              : candidate.role === "solvent"
                                ? "Solvent"
                                : "Reagent",
            };

            // Add to both current reaction and shown reaction if they exist
            state.currentReaction.chemicals.push(newChemical);
            if (state.shownReaction?.reaction) {
                state.shownReaction.reaction.chemicals.push(newChemical);
            }
        });

        // Reset prediction state
        state.prediction.show = false;
        state.prediction.type = null;
        state.prediction.candidates = [];
        state.prediction.selectedCandidates = [];
    });

    // Prediction API actions
    // Reactants prediction
    builder.addCase(predictReactants.request, (state) => {
        state.prediction.loading = true;
        state.prediction.error = null;
    });
    builder.addCase(predictReactants.success, (state, action) => {
        state.prediction.loading = false;
        // Extract candidates from the new SimplePredictionResponse format
        const allCandidates = buildPredictionCandidates(
            action.payload,
            "reactant",
            "reactant"
        );
        state.prediction.candidates = allCandidates;
        state.prediction.error = null;
    });
    builder.addCase(predictReactants.error, (state, action) => {
        state.prediction.loading = false;
        state.prediction.error = resolveRsaaErrorMessage(
            action.payload,
            "Failed to predict reactants"
        );
        state.prediction.candidates = [];
    });

    // Reagents prediction
    builder.addCase(predictReagents.request, (state) => {
        state.prediction.loading = true;
        state.prediction.error = null;
    });
    builder.addCase(predictReagents.success, (state, action) => {
        state.prediction.loading = false;
        // Extract candidates from the new SimplePredictionResponse format
        const allCandidates = buildPredictionCandidates(
            action.payload,
            "reagent",
            "reagent"
        );
        state.prediction.candidates = allCandidates;
        state.prediction.error = null;
    });
    builder.addCase(predictReagents.error, (state, action) => {
        state.prediction.loading = false;
        state.prediction.error = resolveRsaaErrorMessage(
            action.payload,
            "Failed to predict reagents"
        );
        state.prediction.candidates = [];
    });

    // Products prediction
    builder.addCase(predictProducts.request, (state) => {
        state.prediction.loading = true;
        state.prediction.error = null;
    });
    builder.addCase(predictProducts.success, (state, action) => {
        state.prediction.loading = false;
        // Extract candidates from the new SimplePredictionResponse format
        const allCandidates = buildPredictionCandidates(
            action.payload,
            "product",
            "product"
        );
        state.prediction.candidates = allCandidates;
        state.prediction.error = null;
    });
    builder.addCase(predictProducts.error, (state, action) => {
        state.prediction.loading = false;
        state.prediction.error = resolveRsaaErrorMessage(
            action.payload,
            "Failed to predict products"
        );
        state.prediction.candidates = [];
    });

    // All components prediction
    builder.addCase(predictComponents.request, (state) => {
        state.prediction.loading = true;
        state.prediction.error = null;
    });
    builder.addCase(predictComponents.success, (state, action) => {
        state.prediction.loading = false;
        // Extract candidates from the new SimplePredictionResponse format
        const allCandidates = buildPredictionCandidates(
            action.payload,
            "component",
            "component"
        );
        state.prediction.candidates = allCandidates;
        state.prediction.error = null;
    });
    builder.addCase(predictComponents.error, (state, action) => {
        state.prediction.loading = false;
        state.prediction.error = resolveRsaaErrorMessage(
            action.payload,
            "Failed to predict components"
        );
        state.prediction.candidates = [];
    });

    // Catalyst prediction
    builder.addCase(predictCatalyst.request, (state) => {
        state.prediction.loading = true;
        state.prediction.error = null;
    });
    builder.addCase(predictCatalyst.success, (state, action) => {
        state.prediction.loading = false;
        // Extract candidates from the new SimplePredictionResponse format
        const allCandidates = buildPredictionCandidates(
            action.payload,
            "catalyst",
            "catalyst"
        );
        state.prediction.candidates = allCandidates;
        state.prediction.error = null;
    });
    builder.addCase(predictCatalyst.error, (state, action) => {
        state.prediction.loading = false;
        state.prediction.error = resolveRsaaErrorMessage(
            action.payload,
            "Failed to predict catalyst"
        );
        state.prediction.candidates = [];
    });

    // Solvent prediction
    builder.addCase(predictSolvent.request, (state) => {
        state.prediction.loading = true;
        state.prediction.error = null;
    });
    builder.addCase(predictSolvent.success, (state, action) => {
        state.prediction.loading = false;
        // Extract candidates from the new SimplePredictionResponse format
        const allCandidates = buildPredictionCandidates(
            action.payload,
            "solvent",
            "solvent"
        );
        state.prediction.candidates = allCandidates;
        state.prediction.error = null;
    });
    builder.addCase(predictSolvent.error, (state, action) => {
        state.prediction.loading = false;
        state.prediction.error = resolveRsaaErrorMessage(
            action.payload,
            "Failed to predict solvent"
        );
        state.prediction.candidates = [];
    });
});
